import React, { useState } from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import {
  TrendingDown,
  TrendingUp,
  ShieldCheck,
  Zap,
  DollarSign,
  ArrowRight,
  Clock,
  Fuel,
  Sliders,
  Home,
  Building,
  Percent,
  Award,
  Download,
  FileText,
  RotateCcw,
  Sparkles
} from 'lucide-react';
import { generateQuotationPdf } from '../../lib/pdfGenerator';

type FuelType = 'PETROL' | 'DIESEL' | 'BAND_A' | 'MIXED';
type ChartMode = 'CUMULATIVE' | 'MONTHLY_DRAIN' | 'NET_SAVINGS';

interface ScenarioPreset {
  name: string;
  icon: React.ReactNode;
  dailySpend: number;
  fuelType: FuelType;
  description: string;
}

export const DynamicROICalculator: React.FC = () => {
  const { getActivePreset, setIsLeadModalOpen, petrolPrice, dieselPrice, gridTariff } = useSolarStore();
  const preset = getActivePreset();

  // Interactive Inputs
  const [dailyFuelSpend, setDailyFuelSpend] = useState<number>(12000); // Default NGN 12,000 / day
  const [fuelType, setFuelType] = useState<FuelType>('PETROL');
  const [inflationRatePct, setInflationRatePct] = useState<number>(15); // 15% annual fuel price inflation
  const [currency, setCurrency] = useState<'NGN' | 'USD'>('NGN');
  const [chartMode, setChartMode] = useState<ChartMode>('CUMULATIVE');

  const usdRate = 1520; // 1 USD = 1,520 NGN

  // Format Helper
  const formatMoney = (amountNgn: number) => {
    const val = currency === 'USD' ? amountNgn / usdRate : amountNgn;
    const symbol = currency === 'USD' ? '$' : '₦';
    return `${symbol}${Math.round(val).toLocaleString()}`;
  };

  // Quick Preset Scenarios
  const scenarioPresets: ScenarioPreset[] = [
    {
      name: '2-Bed Flat / Small Business',
      icon: <Home className="w-4 h-4" />,
      dailySpend: 6000,
      fuelType: 'PETROL',
      description: 'Tiger / Small Elepaq Generator (~5 hrs/day)'
    },
    {
      name: '3-Bed Duplex Household',
      icon: <Home className="w-4 h-4" />,
      dailySpend: 14000,
      fuelType: 'PETROL',
      description: '7.5kVA Key-Start Petrol Gen (~8 hrs/day)'
    },
    {
      name: 'Executive Villa / Mansion',
      icon: <Building className="w-4 h-4" />,
      dailySpend: 28000,
      fuelType: 'DIESEL',
      description: '15kVA Soundproof Diesel Gen (~10 hrs/day)'
    },
    {
      name: 'Plaza / Supermarket',
      icon: <Building className="w-4 h-4" />,
      dailySpend: 55000,
      fuelType: 'DIESEL',
      description: 'Heavy Commercial Diesel Soundproof Plant'
    }
  ];

  // Core Financial Math
  const monthlyFuelSpend = dailyFuelSpend * 30;
  const systemCapEx = preset.priceNgn;

  // Estimate fuel price per liter based on fuel type
  const effectiveFuelPricePerLiter =
    fuelType === 'DIESEL' ? dieselPrice : fuelType === 'PETROL' ? petrolPrice : petrolPrice * 0.9;

  const monthlyLitersSaved = Math.round(monthlyFuelSpend / effectiveFuelPricePerLiter);
  const annualLitersSaved = monthlyLitersSaved * 12;

  // Payback Months (CapEx / Monthly Fuel Spend)
  const exactPaybackMonths = Math.max(1, systemCapEx / monthlyFuelSpend);
  const paybackMonthsInt = Math.floor(exactPaybackMonths);
  const paybackDaysInt = Math.round((exactPaybackMonths - paybackMonthsInt) * 30);

  // Generate 5-Year Projection with Fuel Inflation
  const generateProjectionData = () => {
    const data = [];
    let cumulativeGenSpend = 0;
    let currentAnnualSpend = monthlyFuelSpend * 12;

    for (let yr = 1; yr <= 5; yr++) {
      if (yr > 1) {
        currentAnnualSpend *= 1 + inflationRatePct / 100;
      }
      // Add annual maintenance overhead for generators (oil, spark plugs, filters, servicing ~ ₦120k/yr)
      const genServicingOverhead = 150000 * Math.pow(1.1, yr - 1);
      const totalGenYearCost = currentAnnualSpend + genServicingOverhead;
      cumulativeGenSpend += totalGenYearCost;

      // Solar CapEx is one-time + minimal maintenance reserve (₦20k/yr)
      const cumulativeSolarCost = systemCapEx + (30000 * yr);
      const netSavings = Math.max(0, cumulativeGenSpend - cumulativeSolarCost);

      data.push({
        year: `Year ${yr}`,
        GeneratorSunkCost: Math.round(currency === 'USD' ? cumulativeGenSpend / usdRate : cumulativeGenSpend),
        AccessTechSolar: Math.round(currency === 'USD' ? cumulativeSolarCost / usdRate : cumulativeSolarCost),
        MonthlyGenDrain: Math.round(currency === 'USD' ? (totalGenYearCost / 12) / usdRate : totalGenYearCost / 12),
        NetCashRetained: Math.round(currency === 'USD' ? netSavings / usdRate : netSavings),
      });
    }
    return data;
  };

  const projectionData = generateProjectionData();
  const year5GenCost = currency === 'USD' ? (projectionData[4].GeneratorSunkCost * usdRate) : (projectionData[4].GeneratorSunkCost);
  const year5SolarCost = currency === 'USD' ? (projectionData[4].AccessTechSolar * usdRate) : (projectionData[4].AccessTechSolar);
  const fiveYearNetSavings = year5GenCost - year5SolarCost;

  // PDF Quote Trigger
  const handleDownloadPdfQuote = () => {
    generateQuotationPdf({
      fullName: 'Valued Nigerian Client',
      whatsappNumber: '08064752074',
      state: 'Abia / Lagos',
      presetName: preset.name,
      capacityKva: preset.capacityKva,
      batteryCapacityKwh: preset.batteryCapacityKwh,
      solarPanelCapacityKw: preset.solarPanelCapacityKw,
      priceNgn: preset.priceNgn,
      dailyFuelSpend: dailyFuelSpend,
      monthlySavings: Math.round(monthlyFuelSpend),
      paybackMonths: Math.round(exactPaybackMonths),
      date: new Date().toLocaleDateString(),
      quotationNo: `ATR-ROI-${Math.floor(100000 + Math.random() * 900000)}`,
    });
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-10 shadow-2xl text-white space-y-10 my-12 relative overflow-hidden">
      {/* Background Accent Glow */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 blur-[120px] rounded-full pointer-events-none" />

      {/* Top Header & Live Engine Badge */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 pb-6 border-b border-slate-800">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5" /> Real-Time Dynamic ROI & Fuel Sunk Cost Engine
          </div>
          <h2 className="text-2xl sm:text-4xl font-black tracking-tight text-white">
            Generator Sunk Cost vs. Access Tech Solar Asset
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 max-w-3xl leading-relaxed">
            Generators are bottomless financial drains that depreciate to zero while burning fuel. Access Tech Tier-1 Solar is an appreciating home asset that pays for itself in months and guarantees 25 years of clean energy.
          </p>
        </div>

        {/* Currency Switcher & Payback Banner */}
        <div className="flex items-center gap-4 shrink-0">
          <div className="bg-slate-950 p-1.5 rounded-xl border border-slate-800 flex items-center text-xs font-mono">
            <span className="text-slate-500 px-2 font-sans font-semibold">Currency:</span>
            <button
              onClick={() => setCurrency('NGN')}
              className={`px-3 py-1 rounded-lg font-bold transition-all ${
                currency === 'NGN' ? 'bg-emerald-500 text-slate-950' : 'text-slate-400 hover:text-white'
              }`}
            >
              NGN (₦)
            </button>
            <button
              onClick={() => setCurrency('USD')}
              className={`px-3 py-1 rounded-lg font-bold transition-all ${
                currency === 'USD' ? 'bg-emerald-500 text-slate-950' : 'text-slate-400 hover:text-white'
              }`}
            >
              USD ($)
            </button>
          </div>

          <div className="bg-slate-950 px-5 py-3 rounded-2xl border border-emerald-500/30 text-right hidden sm:block">
            <span className="text-[10px] text-slate-400 block font-semibold uppercase tracking-wider">Exact Payback Time</span>
            <span className="text-xl sm:text-2xl font-black text-emerald-400 font-mono">
              {paybackMonthsInt}m {paybackDaysInt}d
            </span>
          </div>
        </div>
      </div>

      {/* Quick Scenario Preset Selector Bar */}
      <div className="space-y-3">
        <label className="text-xs font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
          <Sliders className="w-4 h-4 text-emerald-400" /> Select A Benchmark Household Profile:
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {scenarioPresets.map((sc, idx) => (
            <button
              key={idx}
              onClick={() => {
                setDailyFuelSpend(sc.dailySpend);
                setFuelType(sc.fuelType);
              }}
              className={`p-4 rounded-2xl border text-left transition-all space-y-2 group ${
                dailyFuelSpend === sc.dailySpend && fuelType === sc.fuelType
                  ? 'bg-slate-950 border-emerald-500/60 shadow-lg shadow-emerald-500/10'
                  : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className={`p-2 rounded-xl ${dailyFuelSpend === sc.dailySpend ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-800 text-slate-400'}`}>
                  {sc.icon}
                </span>
                <span className="text-xs font-mono font-bold text-amber-400">
                  {formatMoney(sc.dailySpend)}/day
                </span>
              </div>
              <div>
                <div className="text-xs font-bold text-white group-hover:text-emerald-300 transition-colors">
                  {sc.name}
                </div>
                <div className="text-[11px] text-slate-400 mt-0.5 leading-snug">
                  {sc.description}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Interactive Controls & Live Metric Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Controls Column (5 cols) */}
        <div className="lg:col-span-5 space-y-6">
          {/* Daily Fuel Spend Slider Card */}
          <div className="bg-slate-950 p-6 rounded-2xl border border-slate-800 space-y-5 shadow-inner">
            <div className="flex justify-between items-center">
              <label className="text-xs font-bold text-slate-300 flex items-center gap-2">
                <Fuel className="w-4 h-4 text-amber-400" /> Daily Petrol / Diesel Expenditure
              </label>
              <span className="text-amber-400 font-black text-lg font-mono">
                {formatMoney(dailyFuelSpend)} <span className="text-xs text-slate-500 font-sans">/day</span>
              </span>
            </div>

            <input
              type="range"
              min="2000"
              max="80000"
              step="1000"
              value={dailyFuelSpend}
              onChange={(e) => setDailyFuelSpend(Number(e.target.value))}
              className="w-full accent-emerald-500 cursor-pointer h-2 bg-slate-900 rounded-lg"
            />

            <div className="flex justify-between text-[11px] text-slate-500 font-mono">
              <span>{formatMoney(2000)}/day</span>
              <span>{formatMoney(40000)}/day</span>
              <span>{formatMoney(80000)}/day</span>
            </div>

            {/* Fuel Type & Tariff Selector Buttons */}
            <div className="pt-3 border-t border-slate-900 space-y-2">
              <span className="text-[11px] text-slate-400 font-semibold block">Primary Power Source Being Replaced:</span>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setFuelType('PETROL')}
                  className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                    fuelType === 'PETROL' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-slate-900 text-slate-400 hover:text-white'
                  }`}
                >
                  Petrol (₦1,250/L)
                </button>
                <button
                  onClick={() => setFuelType('DIESEL')}
                  className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                    fuelType === 'DIESEL' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' : 'bg-slate-900 text-slate-400 hover:text-white'
                  }`}
                >
                  Diesel (₦1,480/L)
                </button>
                <button
                  onClick={() => setFuelType('BAND_A')}
                  className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                    fuelType === 'BAND_A' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40' : 'bg-slate-900 text-slate-400 hover:text-white'
                  }`}
                >
                  Band A Grid
                </button>
              </div>
            </div>

            {/* Inflation Rate Slider */}
            <div className="pt-3 border-t border-slate-900 space-y-2">
              <div className="flex justify-between text-[11px]">
                <span className="text-slate-400 font-medium flex items-center gap-1">
                  <Percent className="w-3.5 h-3.5 text-rose-400" /> Projected Annual Fuel Price Increase:
                </span>
                <strong className="text-rose-400 font-mono">{inflationRatePct}% / year</strong>
              </div>
              <input
                type="range"
                min="0"
                max="30"
                step="5"
                value={inflationRatePct}
                onChange={(e) => setInflationRatePct(Number(e.target.value))}
                className="w-full accent-rose-500 cursor-pointer h-1.5 bg-slate-900 rounded-lg"
              />
            </div>
          </div>

          {/* Key Live Metric Cards Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800 space-y-1">
              <span className="text-[11px] text-slate-400 block">Monthly Fuel Drain</span>
              <div className="text-xl font-black text-rose-400 font-mono">
                {formatMoney(monthlyFuelSpend)}
              </div>
              <span className="text-[10px] text-slate-500">~{monthlyLitersSaved} Liters/mo burned</span>
            </div>

            <div className="bg-slate-950 p-4 rounded-2xl border border-emerald-500/30 space-y-1">
              <span className="text-[11px] text-emerald-300 block">5-Year Net Cash Retained</span>
              <div className="text-xl font-black text-emerald-400 font-mono">
                {formatMoney(fiveYearNetSavings > 0 ? fiveYearNetSavings : 0)}
              </div>
              <span className="text-[10px] text-emerald-400/80">After 100% solar CapEx pay-off</span>
            </div>
          </div>

          {/* Action CTAs */}
          <div className="space-y-3">
            <button
              onClick={() => setIsLeadModalOpen(true, `ROI Consultation for ${preset.name}`)}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black py-4 rounded-2xl shadow-xl shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 text-xs uppercase tracking-wider"
            >
              Lock In This ROI & Book Engineer Survey
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={handleDownloadPdfQuote}
              className="w-full bg-slate-950 hover:bg-slate-800 text-slate-300 border border-slate-800 font-semibold py-3 rounded-2xl transition-all text-xs flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4 text-emerald-400" /> Download Stamped PDF Quotation
            </button>
          </div>
        </div>

        {/* Right Recharts Column (7 cols) */}
        <div className="lg:col-span-7 bg-slate-950 p-6 sm:p-8 rounded-3xl border border-slate-800 space-y-6 shadow-2xl">
          {/* Chart View Toggle Tabs */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-800">
            <div>
              <h3 className="font-extrabold text-white text-base flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-emerald-400" />
                5-Year Financial Projection & Wealth Preservation
              </h3>
              <p className="text-xs text-slate-400">
                Comparison of cumulative money lost in generator fuel vs solar system ownership.
              </p>
            </div>

            <div className="flex items-center gap-1 bg-slate-900 p-1 rounded-xl border border-slate-800 text-xs font-medium">
              <button
                onClick={() => setChartMode('CUMULATIVE')}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  chartMode === 'CUMULATIVE' ? 'bg-emerald-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'
                }`}
              >
                5-Yr Total
              </button>
              <button
                onClick={() => setChartMode('MONTHLY_DRAIN')}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  chartMode === 'MONTHLY_DRAIN' ? 'bg-emerald-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'
                }`}
              >
                Monthly Drain
              </button>
              <button
                onClick={() => setChartMode('NET_SAVINGS')}
                className={`px-3 py-1.5 rounded-lg transition-all ${
                  chartMode === 'NET_SAVINGS' ? 'bg-emerald-500 text-slate-950 font-bold' : 'text-slate-400 hover:text-white'
                }`}
              >
                Savings Curve
              </button>
            </div>
          </div>

          {/* Recharts Render Container */}
          <div className="h-[320px] w-full pt-2">
            <ResponsiveContainer width="100%" height="100%">
              {chartMode === 'CUMULATIVE' ? (
                <BarChart data={projectionData} margin={{ top: 20, right: 20, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="year" stroke="#64748b" fontSize={12} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(val) => currency === 'USD' ? `$${val}` : `₦${val / 1000000}M`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#090d16', borderColor: '#334155', borderRadius: '12px', fontSize: '12px' }}
                    formatter={(value: any) => [formatMoney(Number(value) * (currency === 'USD' ? usdRate : 1)), 'Cumulative Cost']}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                  <Bar dataKey="GeneratorSunkCost" name="Generator Sunk Cost (Lost Money)" fill="#f43f5e" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="AccessTechSolar" name="Access Tech Solar Asset (One-Time CapEx)" fill="#10b981" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : chartMode === 'MONTHLY_DRAIN' ? (
                <BarChart data={projectionData} margin={{ top: 20, right: 20, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="year" stroke="#64748b" fontSize={12} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(val) => currency === 'USD' ? `$${val}` : `₦${val / 1000}k`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#090d16', borderColor: '#334155', borderRadius: '12px', fontSize: '12px' }}
                    formatter={(value: any) => [formatMoney(Number(value) * (currency === 'USD' ? usdRate : 1)), 'Monthly Outflow']}
                  />
                  <Legend wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }} />
                  <Bar dataKey="MonthlyGenDrain" name="Monthly Generator Fuel Outflow (Increasing)" fill="#fbbf24" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : (
                <AreaChart data={projectionData} margin={{ top: 20, right: 20, left: 20, bottom: 5 }}>
                  <defs>
                    <linearGradient id="colorSavings" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.5} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="year" stroke="#64748b" fontSize={12} tickLine={false} />
                  <YAxis stroke="#64748b" fontSize={11} tickLine={false} tickFormatter={(val) => currency === 'USD' ? `$${val}` : `₦${val / 1000000}M`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#090d16', borderColor: '#334155', borderRadius: '12px', fontSize: '12px' }}
                    formatter={(value: any) => [formatMoney(Number(value) * (currency === 'USD' ? usdRate : 1)), 'Net Wealth Retained']}
                  />
                  <Area type="monotone" dataKey="NetCashRetained" name="Net Wealth Retained in Pocket" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorSavings)" />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>

          {/* Financial Breakdown Badges Footer */}
          <div className="pt-4 border-t border-slate-900 grid grid-cols-2 sm:grid-cols-3 gap-3 text-[11px] text-slate-400">
            <div className="bg-slate-900 p-2.5 rounded-xl border border-slate-800">
              <span className="block text-slate-500 font-medium">Selected Package</span>
              <strong className="text-white text-xs">{preset.name}</strong>
            </div>
            <div className="bg-slate-900 p-2.5 rounded-xl border border-slate-800">
              <span className="block text-slate-500 font-medium">CapEx Outlay</span>
              <strong className="text-emerald-400 text-xs font-mono">{formatMoney(systemCapEx)}</strong>
            </div>
            <div className="bg-slate-900 p-2.5 rounded-xl border border-slate-800 col-span-2 sm:col-span-1">
              <span className="block text-slate-500 font-medium">Annual Fuel Saved</span>
              <strong className="text-amber-400 text-xs font-mono">{annualLitersSaved.toLocaleString()} Liters</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
